import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { RoomType, CreateRoomTypeRequest } from '../types/rooms';
import { roomService } from '../services/roomService';

interface FormState {
  name: string;
  description: string;
  max_capacity: number;
  amenities: string;
}

const emptyForm: FormState = { name: '', description: '', max_capacity: 2, amenities: '' };

export default function RoomTypesPage() {
  const { t } = useTranslation();
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await roomService.listRoomTypes();
      setRoomTypes(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (rt: RoomType) => {
    setEditingId(rt.id);
    setForm({
      name: rt.name,
      description: rt.description || '',
      max_capacity: rt.max_capacity,
      amenities: (rt.amenities || []).join(', '),
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError(t('roomTypes.name') + ' est obligatoire'); return; }
    if (form.max_capacity < 1) { setFormError(t('roomTypes.capacity') + ' doit être >= 1'); return; }

    const payload: CreateRoomTypeRequest = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      max_capacity: form.max_capacity,
      amenities: form.amenities ? form.amenities.split(',').map(s => s.trim()).filter(Boolean) : [],
    };

    try {
      setSaving(true);
      setFormError(null);
      if (editingId) {
        await roomService.updateRoomType(editingId, payload);
      } else {
        await roomService.createRoomType(payload);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await roomService.deleteRoomType(id);
      setDeleteConfirm(null);
      await load();
    } catch (e: any) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('roomTypes.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('roomTypes.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/rooms" className="px-4 py-2 border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50 transition text-sm">
            {t('nav.rooms')}
          </Link>
          <button onClick={openCreate} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition">
            {t('roomTypes.create')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={load} className="text-sm underline">{t('common.retry')}</button>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">{editingId ? t('roomTypes.edit') : t('roomTypes.create')}</h2>
          {formError && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{formError}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('roomTypes.name')}</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" maxLength={100} required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('roomTypes.capacity')}</label>
                <input type="number" value={form.max_capacity} onChange={e => setForm(f => ({ ...f, max_capacity: parseInt(e.target.value) || 1 }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" min={1} required />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('roomTypes.description')}</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('roomTypes.amenities')} (séparés par des virgules)</label>
              <input type="text" value={form.amenities} onChange={e => setForm(f => ({ ...f, amenities: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" placeholder="WiFi, TV, Minibar" />
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
                {saving ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : roomTypes.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{t('roomTypes.noData')}</div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('roomTypes.name')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('roomTypes.capacity')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('roomTypes.description')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('roomTypes.amenities')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('roomTypes.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {roomTypes.map(rt => (
                <tr key={rt.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{rt.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{rt.max_capacity}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{rt.description || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {(rt.amenities || []).length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {rt.amenities.map((a, i) => (
                          <span key={i} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">{a}</span>
                        ))}
                      </div>
                    ) : '—'}
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button onClick={() => openEdit(rt)} className="text-purple-600 hover:text-purple-800">{t('roomTypes.edit')}</button>
                    {deleteConfirm === rt.id ? (
                      <>
                        <button onClick={() => handleDelete(rt.id)} className="text-red-600 hover:text-red-800 font-medium">{t('common.confirm')}</button>
                        <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 hover:text-gray-700">{t('common.cancel')}</button>
                      </>
                    ) : (
                      <button onClick={() => setDeleteConfirm(rt.id)} className="text-red-500 hover:text-red-700">{t('roomTypes.delete')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
